import type { Ref } from 'vue';
import type {
    ImageEditorCropMode,
    ImageEditorDimensions,
    ImageEditorExportFormat
} from './imageEditor';
import type {
    CropperHistoryState,
    CropSelection,
    WatermarkType
} from './cropper';

interface CropperHistoryActionsContext
{
    history: Ref<CropperHistoryState[]>;
    historyIndex: Ref<number>;
    cropSelection: CropSelection;
    cropMode: Ref<ImageEditorCropMode>;
    aspectRatio: Ref<string>;
    fixedSize: ImageEditorDimensions;
    circularSize: Ref<number>;
    templateSize: Ref<ImageEditorDimensions | null>;
    rotation: Ref<number>;
    flipHorizontal: Ref<boolean>;
    flipVertical: Ref<boolean>;
    scale: Ref<number>;
    brightness: Ref<number>;
    contrast: Ref<number>;
    saturation: Ref<number>;
    grayscale: Ref<number>;
    watermarkEnabled: Ref<boolean>;
    watermarkText: Ref<string>;
    watermarkType: Ref<WatermarkType>;
    watermarkX: Ref<number>;
    watermarkY: Ref<number>;
    watermarkOpacity: Ref<number>;
    watermarkSize: Ref<number>;
    exportFormat: Ref<ImageEditorExportFormat>;
    quality: Ref<number>;
    outputResizeEnabled: Ref<boolean>;
    outputSize: ImageEditorDimensions;
    optimizeDimensions: Ref<boolean>;
    redrawCanvas: () => void;
}

export function createCropperHistoryActions(context: CropperHistoryActionsContext)
{
    function saveHistory(label: string)
    {
        const snapshot: CropperHistoryState = {
            id: crypto.randomUUID(),
            label,
            cropSelection: { ...context.cropSelection },
            cropMode: context.cropMode.value,
            aspectRatio: context.aspectRatio.value,
            fixedSize: { ...context.fixedSize },
            circularSize: context.circularSize.value,
            templateSize: context.templateSize.value ? { ...context.templateSize.value } : null,
            rotation: context.rotation.value,
            flipHorizontal: context.flipHorizontal.value,
            flipVertical: context.flipVertical.value,
            scale: context.scale.value,
            brightness: context.brightness.value,
            contrast: context.contrast.value,
            saturation: context.saturation.value,
            grayscale: context.grayscale.value,
            watermarkEnabled: context.watermarkEnabled.value,
            watermarkText: context.watermarkText.value,
            watermarkType: context.watermarkType.value,
            watermarkX: context.watermarkX.value,
            watermarkY: context.watermarkY.value,
            watermarkOpacity: context.watermarkOpacity.value,
            watermarkSize: context.watermarkSize.value,
            exportFormat: context.exportFormat.value,
            quality: context.quality.value,
            outputResizeEnabled: context.outputResizeEnabled.value,
            outputSize: { ...context.outputSize },
            optimizeDimensions: context.optimizeDimensions.value
        };

        context.history.value = context.history.value.slice(0, context.historyIndex.value + 1);
        context.history.value.push(snapshot);

        if (context.history.value.length > 30)
        {
            context.history.value.shift();
        }

        context.historyIndex.value = context.history.value.length - 1;
    }

    function restoreHistory(snapshot: CropperHistoryState)
    {
        Object.assign(context.cropSelection, snapshot.cropSelection);
        context.cropMode.value = snapshot.cropMode;
        context.aspectRatio.value = snapshot.aspectRatio;
        context.fixedSize.width = snapshot.fixedSize.width;
        context.fixedSize.height = snapshot.fixedSize.height;
        context.circularSize.value = snapshot.circularSize;
        context.templateSize.value = snapshot.templateSize ? { ...snapshot.templateSize } : null;
        context.rotation.value = snapshot.rotation;
        context.flipHorizontal.value = snapshot.flipHorizontal;
        context.flipVertical.value = snapshot.flipVertical;
        context.scale.value = snapshot.scale;
        context.brightness.value = snapshot.brightness;
        context.contrast.value = snapshot.contrast;
        context.saturation.value = snapshot.saturation;
        context.grayscale.value = snapshot.grayscale;
        context.watermarkEnabled.value = snapshot.watermarkEnabled;
        context.watermarkText.value = snapshot.watermarkText;
        context.watermarkType.value = snapshot.watermarkType;
        context.watermarkX.value = snapshot.watermarkX;
        context.watermarkY.value = snapshot.watermarkY;
        context.watermarkOpacity.value = snapshot.watermarkOpacity;
        context.watermarkSize.value = snapshot.watermarkSize;
        context.exportFormat.value = snapshot.exportFormat;
        context.quality.value = snapshot.quality;
        context.outputResizeEnabled.value = snapshot.outputResizeEnabled;
        context.outputSize.width = snapshot.outputSize.width;
        context.outputSize.height = snapshot.outputSize.height;
        context.optimizeDimensions.value = snapshot.optimizeDimensions;
        context.redrawCanvas();
    }

    function undo()
    {
        if (context.historyIndex.value <= 0)
            return;

        context.historyIndex.value--;
        restoreHistory(context.history.value[context.historyIndex.value]);
    }

    function redo()
    {
        if (context.historyIndex.value >= context.history.value.length - 1)
            return;

        context.historyIndex.value++;
        restoreHistory(context.history.value[context.historyIndex.value]);
    }

    return {
        saveHistory,
        undo,
        redo
    };
}
